import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AuthContext } from "../../contexts/AuthContext";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import {
  getServerErrorMessage,
  mapAuthErrorMessage,
} from "../../utils/authErrorMessages";

const AUTH_LOGIN_URL = "/auth?request_type=login";

const AuthVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get("type");
  const {
    google_auth_challenge,
    google_auth,
    reset_google_auth_challenge,
    reset_google_auth,
    verify_google_auth,
    verify_login,
    readAuthVerificationSession,
    updateAuthVerificationSession,
    removeAuthVerificationSession,
    switchAccountForVerification,
    global_authState,
  } = useContext(AuthContext);
  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();
  const initializedRef = useRef(false);

  const [session, setSession] = useState(() => readAuthVerificationSession?.());
  const [verificationCode, setVerificationCode] = useState("");
  const [setupInfo, setSetupInfo] = useState(() => {
    const saved = readAuthVerificationSession?.();
    return saved?.type === "setup"
      ? {
          isSendEmail: saved?.isSendEmail,
          email: saved?.email,
        }
      : null;
  });
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [requiresRelogin, setRequiresRelogin] = useState(false);
  const [reloginWarning, setReloginWarning] = useState("");
  const [activeChallengeToken, setActiveChallengeToken] = useState(
    session?.challengeToken || ""
  );

  const type = useMemo(() => {
    if (requestedType === "setup" || requestedType === "login") {
      return requestedType;
    }
    return session?.type === "setup" ? "setup" : "login";
  }, [requestedType, session?.type]);

  const isSetup = type === "setup";
  const email =
    setupInfo?.email || session?.email || session?.user?.email || "";
  const missingVerificationSession =
    !session || (!isSetup && !activeChallengeToken);

  useEffect(() => {
    if (global_authState?.uid) {
      window.location.replace("/consolePanel");
    }
  }, [global_authState?.uid]);

  useEffect(() => {
    if (!isSetup || initializedRef.current) return;
    initializedRef.current = true;
    setLoadingSetup(true);
    const setupRequest = activeChallengeToken
      ? google_auth_challenge(activeChallengeToken)
      : google_auth();
    setupRequest
      .then((data) => {
        const nextInfo = {
          isSendEmail: data?.isSendEmail,
          email: data?.email || email,
        };
        updateAuthVerificationSession?.({
          type: "setup",
          ...nextInfo,
        });
        setSetupInfo(nextInfo);
        setSession((prev) => ({
          ...(prev || {}),
          type: "setup",
          ...nextInfo,
        }));
      })
      .catch((error) => {
        handleError(
          mapAuthErrorMessage(
            getServerErrorMessage(error),
            "Could not start verification. Please sign in again."
          )
        );
      })
      .finally(() => setLoadingSetup(false));
  }, [
    activeChallengeToken,
    email,
    google_auth,
    google_auth_challenge,
    handleError,
    isSetup,
    updateAuthVerificationSession,
  ]);

  useEffect(() => {
    setActiveChallengeToken(session?.challengeToken || "");
    setRequiresRelogin(false);
    setReloginWarning("");
  }, [session?.challengeToken]);

  const handleBackToLogin = async () => {
    removeAuthVerificationSession?.();
    await switchAccountForVerification?.();
    window.location.replace(AUTH_LOGIN_URL);
  };

  const handleResendQRCode = async () => {
    setResending(true);
    try {
      const data = activeChallengeToken
        ? await reset_google_auth_challenge(activeChallengeToken)
        : await reset_google_auth();
      setSetupInfo({
        isSendEmail: data?.isSendEmail,
        email: data?.email || email,
      });
      updateAuthVerificationSession?.({
        type: "setup",
        isSendEmail: data?.isSendEmail,
        email: data?.email || email,
      });
      handleSuccess("We have resent the QR Code email to your email.");
    } catch (error) {
      handleError("Failed to resend QR Code email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleSetupVerify = async () => {
    if (verifying || !verificationCode.trim()) return;
    setVerifying(true);
    try {
      const res = activeChallengeToken
        ? await verify_login({
            verificationCode,
            challengeToken: activeChallengeToken,
          })
        : await verify_google_auth({ verificationCode });
      if (!res) {
        handleError("Verification failed, please try again.");
      }
    } catch (error) {
      handleError("Verification failed, please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleLoginVerify = async () => {
    if (verifying || !verificationCode.trim()) return;
    if (!activeChallengeToken) {
      handleError("Sign-in session is missing. Please try logging in again.");
      return;
    }

    setVerifying(true);
    try {
      const res = await verify_login({
        verificationCode,
        challengeToken: activeChallengeToken,
      });

      if (!res) {
        handleError("Verification failed. Try again.");
      }
    } catch (error) {
      const rootError = error?.error || error;
      const attemptsRemainingRaw =
        rootError?.response?.data?.data?.attemptsRemaining ??
        rootError?.response?.data?.attemptsRemaining ??
        rootError?.data?.attemptsRemaining ??
        rootError?.attemptsRemaining;
      const attemptsRemaining =
        attemptsRemainingRaw == null ? null : Number(attemptsRemainingRaw);

      const renewedChallengeToken =
        rootError?.response?.data?.data?.challengeToken ||
        rootError?.response?.data?.challengeToken ||
        rootError?.data?.challengeToken ||
        rootError?.challengeToken ||
        null;

      if (renewedChallengeToken) {
        setActiveChallengeToken(renewedChallengeToken);
        updateAuthVerificationSession?.({
          type: "login",
          challengeToken: renewedChallengeToken,
        });
        setSession((prev) => ({
          ...(prev || {}),
          type: "login",
          challengeToken: renewedChallengeToken,
        }));
      }

      const raw = getServerErrorMessage(rootError);
      const loweredRaw = (raw || "").toLowerCase();
      const shouldForceRelogin =
        (attemptsRemaining !== null && attemptsRemaining <= 0) ||
        loweredRaw.includes("maximum attempts reached") ||
        loweredRaw.includes("please login again") ||
        loweredRaw.includes("token is invalid, expired, or already used");

      if (shouldForceRelogin) {
        setRequiresRelogin(true);
        setReloginWarning(raw || "Session expired. Please sign in again.");
        return;
      }

      handleError(mapAuthErrorMessage(raw, "Invalid or expired code."));
    } finally {
      setVerifying(false);
    }
  };

  const handleVerify = () => {
    if (isSetup) {
      handleSetupVerify();
      return;
    }
    handleLoginVerify();
  };

  const title = isSetup ? "Set Up Google Authenticator" : "Confirm Your Login";
  const helperText = isSetup
    ? "Use Google Authenticator to scan the QR code from your email, then enter the code."
    : "Open your authenticator app to view the code.";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#f7f9fc",
      }}
    >
      <Dialog
        open
        maxWidth='xs'
        fullWidth
        disableEscapeKeyDown
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 4,
            padding: 4,
            boxShadow: "0px 12px 36px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.5rem",
            pb: 1,
          }}
        >
          {title}
        </DialogTitle>

        <Divider sx={{ mb: 2 }} />

        <Box
          component='form'
          onSubmit={(event) => {
            event.preventDefault();
            if (requiresRelogin || missingVerificationSession) {
              handleBackToLogin();
              return;
            }
            handleVerify();
          }}
        >
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant='body1'
                color='text.secondary'
                align='center'
                sx={{
                  maxWidth: "320px",
                  fontSize: "0.95rem",
                  lineHeight: 1.5,
                  mb: 1,
                }}
              >
                {isSetup && email ? (
                  <>
                    We sent the QR code to <strong>{email}</strong>. Use Google
                    Authenticator to scan the QR code from your email, then
                    enter the code.
                  </>
                ) : (
                  <>
                    To protect your account, please verify this login using the
                    6-digit code from your <strong>Google Authenticator</strong>{" "}
                    app.
                  </>
                )}
              </Typography>

              {loadingSetup && (
                <Stack direction='row' spacing={1.25} alignItems='center'>
                  <CircularProgress size={18} />
                  <Typography variant='body2' color='text.secondary'>
                    Preparing verification...
                  </Typography>
                </Stack>
              )}

              {missingVerificationSession && (
                <Alert severity='warning' sx={{ width: "100%" }}>
                  Verification session expired. Please sign in again.
                </Alert>
              )}

              {requiresRelogin && (
                <Typography
                  variant='body2'
                  color='error.main'
                  align='center'
                  sx={{ maxWidth: "320px", fontWeight: 600 }}
                >
                  {reloginWarning}
                </Typography>
              )}

              <TextField
                label='Verification Code'
                value={verificationCode}
                onChange={(event) => setVerificationCode(event.target.value)}
                fullWidth
                margin='dense'
                variant='outlined'
                placeholder='000000'
                autoComplete='off'
                autoFocus
                disabled={
                  loadingSetup || requiresRelogin || missingVerificationSession
                }
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  style: {
                    textAlign: "center",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    letterSpacing: "0.2rem",
                    padding: "12px 14px",
                  },
                }}
                sx={{
                  width: "100%",
                  maxWidth: "280px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <Typography
                id='verification-helper'
                variant='caption'
                color='text.secondary'
                align='center'
                sx={{
                  fontSize: "0.8rem",
                  opacity: 0.8,
                }}
              >
                {helperText}
              </Typography>

              {isSetup && (
                <Button
                  onClick={handleResendQRCode}
                  color='secondary'
                  variant='outlined'
                  disabled={
                    loadingSetup || resending || missingVerificationSession
                  }
                  startIcon={
                    resending ? <CircularProgress size={16} /> : <RefreshIcon />
                  }
                  sx={{ textTransform: "none" }}
                >
                  Resend QR Code Email
                </Button>
              )}
            </Box>
          </DialogContent>

          <Divider sx={{ my: 3 }} />

          <DialogActions
            sx={{
              justifyContent: "center",
              pb: 2,
              gap: 1.25,
              flexDirection: { xs: "column-reverse", sm: "row" },
            }}
          >
            <Button
              color='primary'
              variant='contained'
              type='submit'
              disabled={
                verifying ||
                loadingSetup ||
                (!requiresRelogin &&
                  !missingVerificationSession &&
                  !verificationCode.trim())
              }
              sx={{
                minWidth: 160,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: "bold",
                height: 45,
              }}
            >
              {verifying ? (
                <CircularProgress size={24} color='inherit' />
              ) : requiresRelogin || missingVerificationSession ? (
                "Restart Login"
              ) : (
                "Verify Code"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AuthVerificationPage;
